import { z } from 'zod';
import { StudentSearch } from './fields.js';
/** Admin Students page: list, detail, ban, soft delete and the two resets. */
export const StudentStatusDto = z.enum(['active', 'banned', 'deleted']);
export const StudentListQuery = z
    .object({
    /** `all` hides deleted students; `deleted` shows only them. */
    filter: z.enum(['all', 'active', 'banned', 'deleted']).optional(),
    plan: z.enum(['any', 'paid', 'free']).optional(),
    /** Telegram id, @username or name. */
    q: StudentSearch.optional(),
    sort: z.enum(['active', 'new']).optional(),
    page: z.coerce.number().int().min(1).max(100_000).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
})
    .strict();
export const StudentRowDto = z.object({
    userId: z.uuid(),
    telegramUserId: z.string(),
    username: z.string().nullable(),
    firstName: z.string().nullable(),
    status: StudentStatusDto,
    createdAt: z.string(),
    lastActiveAt: z.string().nullable(),
    streak: z.number(),
    bestStreak: z.number(),
    /** Display name of the live Pro plan; null for a free student. */
    plan: z.string().nullable(),
    planUntil: z.string().nullable(),
});
export const StudentListDto = z.object({
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
    students: z.array(StudentRowDto),
});
const reason = z.string().trim().min(3).max(300);
/** Every action needs an explicit `confirm: true`; the reason is optional except for a ban. */
export const StudentActionRequest = z
    .object({ confirm: z.literal(true), reason: reason.optional() })
    .strict();
export const StudentBanRequest = z.object({ confirm: z.literal(true), reason }).strict();
export const StudentDetailDto = z.object({
    student: z.object({
        userId: z.uuid(),
        telegramUserId: z.string(),
        username: z.string().nullable(),
        firstName: z.string().nullable(),
        locale: z.string(),
        status: StudentStatusDto,
        createdAt: z.string(),
        consentAt: z.string().nullable(),
        banned: z
            .object({ at: z.string(), reason: z.string().nullable(), by: z.string().nullable() })
            .nullable(),
        deleted: z
            .object({
            at: z.string(),
            reason: z.string().nullable(),
            by: z.string().nullable(),
            byLearner: z.boolean(),
        })
            .nullable(),
    }),
    profile: z.object({
        streak: z.number(),
        bestStreak: z.number(),
        lastDoneDate: z.string().nullable(),
        lastActiveAt: z.string().nullable(),
        dailyMinutes: z.number(),
        source: z.string().nullable(),
        limitsResetAt: z.string().nullable(),
        progressResetAt: z.string().nullable(),
        examKey: z.string().nullable(),
        examDate: z.string().nullable(),
    }),
    plan: z.object({
        name: z.string().nullable(),
        until: z.string().nullable(),
        mockCredits: z.number(),
        doubtCredits: z.number(),
        passes: z.array(z.object({
            name: z.string(),
            type: z.string(),
            source: z.string(),
            validUntil: z.string(),
            revoked: z.boolean(),
        })),
    }),
    today: z.object({
        practiceAnswered: z.number(),
        doubtsUsed: z.number(),
        doubtsLimit: z.number().nullable(),
        doubtsWindow: z.enum(['day', 'week', 'month']).nullable(),
        dailyDone: z.boolean(),
    }),
    totals: z.object({
        practiceAnswers: z.number(),
        mockAttempts: z.number(),
        doubts: z.number(),
        openMistakes: z.number(),
    }),
    grandMocks: z.array(z.object({
        id: z.uuid(),
        title: z.string(),
        startsAt: z.string(),
        status: z.string(),
        agreed: z.boolean(),
        attemptId: z.string().nullable(),
        rank: z.number().nullable(),
        score: z.number().nullable(),
        maxScore: z.number().nullable(),
        disqualified: z.boolean(),
    })),
    activity: z.array(z.object({ at: z.string(), kind: z.enum(['practice', 'mock', 'doubt']), text: z.string() })),
    /** What admins did to this student (newest first). */
    history: z.array(z.object({
        at: z.string(),
        action: z.string(),
        by: z.string().nullable(),
        reason: z.string().nullable(),
    })),
});
export const StudentResetDto = z.object({
    ok: z.literal(true),
    resetAt: z.string(),
    streakBefore: z.number().optional(),
    topicsCleared: z.number().optional(),
    mistakesCleared: z.number().optional(),
});
export const StudentOkDto = z.object({ ok: z.literal(true) });
//# sourceMappingURL=students.js.map